import { breakpoints } from '@coursera/cds-core';
import { track } from '@coursera/event-pulse/sdk';

import _t from 'i18n!nls/composable-page-header';

// The default/minimum height of the page header, as this is the default height of the mobile and desktop main navs.
export const DEFAULT_PAGE_HEADER_HEIGHT = 65;

export const PAGE_HEADER_Z_INDEX = 3000;

/**
 * @deprecated Use `breakpoints.values.md` from `@coursera/cds-core` directly instead.
 * This was originally set to the old CUI breakpoint to maintain parity with the previous header.
 * Now that the composable page header is fully rolled out, it aligns with CDS breakpoints.
 * This constant may be removed in a future cleanup.
 */
export const MOBILE_HEADER_BREAKPOINT = breakpoints.values.md;

/**
 * @deprecated Build your media query using `breakpoints.values.md` from `@coursera/cds-core` directly,
 * or use the CDS `useMediaQuery(breakpoints.down('md'))` / theme breakpoint utilities instead.
 */
export const MOBILE_HEADER_BREAKPOINT_MEDIA_QUERY = `@media screen and (max-width: ${MOBILE_HEADER_BREAKPOINT}px)`;

// I noticed our CDS breakpoints use 0.05px to avoid the max-width/min-width collisions, so that's why I'm using that here.
// For example, the lg breakpoint is 1440px, but we have a max-width of 1439.95px for anything below.
export const DESKTOP_HEADER_BREAKPOINT_MEDIA_QUERY = `@media screen and (min-width: ${
  MOBILE_HEADER_BREAKPOINT + 0.05
}px)`;

export const DEFAULT_META_NAV_HEIGHT = 40;

export const DEFAULT_ICON_BUTTON_HEIGHT = 32;

export const DEFAULT_ICON_BUTTON_SVG_HEIGHT = 24;

export const DEFAULT_ICON_BUTTON_PADDING = 4;

export const MOBILE_MAIN_IDENTIFIER = {
  ACCOUNT_MENU: 'ACCOUNT_MENU',
  LANGUAGE_MENU: 'LANGUAGE_MENU',
  EXPLORE_MENU: 'EXPLORE_MENU',
  EXPLORE_ROLES_MENU: 'EXPLORE_ROLES_MENU',
  EXPLORE_CATEGORIES_MENU: 'EXPLORE_CATEGORIES_MENU',
  EXPLORE_SKILLS_MENU: 'EXPLORE_SKILLS_MENU',
  EARN_PROFESSIONAL_CERTIFICATE_MENU: 'EARN_PROFESSIONAL_CERTIFICATE_MENU',
  EARN_ONLINE_DEGREE_MENU: 'EARN_ONLINE_DEGREE_MENU',
  MAIN_MENU: 'MAIN_MENU',
  EXPLORE_PROGRAM: 'EXPLORE_PROGRAM',
  EXPLORE_ORGANIZATION: 'EXPLORE_ORGANIZATION',
  EXPLORE_MORE: 'EXPLORE_MORE',
} as const;

export const MOBILE_LANGUAGE_IDENTIFIER = {
  LANGUAGE_SUBMENU: 'LANGUAGE_SUBMENU',
  LANGUAGE_FEEDBACK: 'LANGUAGE_FEEDBACK',
};

export const MOBILE_EXPLORE_IDENTIFIER = {
  DEGREES_MENU: 'DEGREES_MENU',
  CERTIFICATE_MENU: 'CERTIFICATE_MENU',
  DOMAIN_MENU: 'DOMAIN_MENU',
  ENTERPRISE_EXPLORE_SUBMENU: 'ENTERPRISE_EXPLORE_SUBMENU',
  CONSUMER_EXPLORE_SUBMENU: 'CONSUMER_EXPLORE_SUBMENU',
  CONSUMER_EXPLORE_ROLES_SUBMENU: 'CONSUMER_EXPLORE_ROLES_SUBMENU',
  CONSUMER_EXPLORE_CATEGORIES_SUBMENU: 'CONSUMER_EXPLORE_CATEGORIES_SUBMENU',
  CONSUMER_EXPLORE_SKILLS_SUBMENU: 'CONSUMER_EXPLORE_SKILLS_SUBMENU',
  CONSUMER_EXPLORE_PROFESSIONAL_CERTIFICATE_SUBMENU: 'CONSUMER_EXPLORE_PROFESSIONAL_CERTIFICATE_SUBMENU',
  CONSUMER_EXPLORE_ONLINE_DEGREE_SUBMENU: 'CONSUMER_EXPLORE_ONLINE_DEGREE_SUBMENU',
  ENTERPRISE_EXPLORE_SKILLS_SUBMENU: 'ENTERPRISE_EXPLORE_SKILLS_SUBMENU',
  ENTERPRISE_EXPLORE_PROGRAM_SUBMENU: 'ENTERPRISE_EXPLORE_PROGRAM_SUBMENU',
  ENTERPRISE_EXPLORE_ORGANIZATION_SUBMENU: 'ENTERPRISE_EXPLORE_ORGANIZATION_SUBMENU',
  ENTERPRISE_EXPLORE_CATEGORIES_SUBMENU: 'ENTERPRISE_EXPLORE_CATEGORIES_SUBMENU',
  ENTERPRISE_EXPLORE_MORE_SUBMENU: 'ENTERPRISE_EXPLORE_MORE_SUBMENU',
} as const;

export type MobileMainIdentifierKeys = keyof typeof MOBILE_MAIN_IDENTIFIER;
export type MobileMainIdentifierValues = (typeof MOBILE_MAIN_IDENTIFIER)[MobileMainIdentifierKeys];

export type MobileExploreIdentifierKeys = keyof typeof MOBILE_EXPLORE_IDENTIFIER;
export type MobileExploreIdentifierValues = (typeof MOBILE_EXPLORE_IDENTIFIER)[MobileExploreIdentifierKeys];

export type MobileLanguageIdentifierKeys = keyof typeof MOBILE_LANGUAGE_IDENTIFIER;
export type MobileLanguageIdentifierValues = (typeof MOBILE_LANGUAGE_IDENTIFIER)[MobileLanguageIdentifierKeys];

export const browseAllSubjectsButtonProps = () => ({
  href: '/browse',
  label: _t('Browse all subjects'),
  name: 'browse',
  onButtonClick: () => {
    track('click_megamenu_item', {
      megamenuItemLink: '/browse',
      megamenuItemName: 'browse',
      megamenuItemType: 'view_all_link',
      megamenuSection: 'subjects',
    });
  },
});

export const domainButtonProps = () => [
  {
    label: _t('Data Science'),
    name: 'data-science',
  },
  {
    label: _t('Business'),
    name: 'business',
  },
  {
    label: _t('Computer Science'),
    name: 'computer-science',
  },
  {
    label: _t('Information Technology'),
    name: 'information-technology',
  },
  {
    label: _t('Language Learning'),
    name: 'language-learning',
  },
  {
    label: _t('Health'),
    name: 'life-sciences',
  },
  {
    label: _t('Personal Development'),
    name: 'personal-development',
  },
  {
    label: _t('Physical Science and Engineering'),
    name: 'physical-science-and-engineering',
  },
  {
    label: _t('Social Sciences'),
    name: 'social-sciences',
  },
  {
    label: _t('Arts and Humanities'),
    name: 'arts-and-humanities',
  },
  {
    label: _t('Math and Logic'),
    name: 'math-and-logic',
  },
];

export const goalsButtonsProps = () => {
  const buttons = {
    freeCourse: {
      href: '/courses?query=free',
      label: _t('Take a free course'),
      name: 'free-course',
      onButtonClick: () => {
        track('click_megamenu_item', {
          megamenuItemLink: '/courses?query=free',
          megamenuItemName: 'free-course',
          megamenuItemType: 'other',
          megamenuSection: 'goals',
        });
      },
    },
    degree: {
      label: _t('Earn a Degree'),
      name: 'earn-a-degree',
      trackingName: 'mobile_header_degree_button',
      onButtonClick: () => {
        track('view_megamenu_items', {
          megamenuItemName: 'degree',
          megamenuSection: 'goals',
        });
      },
      exploreViewState: MOBILE_EXPLORE_IDENTIFIER.DEGREES_MENU,
      ariaLabel: _t('Go to degree navigation slide'),
    },
    cert: {
      label: _t('Earn a Certificate'),
      name: 'earn-a-certificate',
      trackingName: 'mobile_header_certificate_button',
      onButtonClick: () => {
        track('view_megamenu_items', {
          megamenuItemName: 'earn-a-certificate',
          megamenuSection: 'goals',
        });
      },
      exploreViewState: MOBILE_EXPLORE_IDENTIFIER.CERTIFICATE_MENU,
      ariaLabel: _t('Go to certificate navigation slide'),
    },
    career: {
      href: '/career-academy',
      label: _t('Find your new career'),
      name: 'career',
      onButtonClick: () => {
        track('view_megamenu_items', {
          megamenuItemName: 'find-new-career',
          megamenuSection: 'goals',
        });
      },
    },
  };
  return buttons;
};

/**
 * List of definitions for deep-linkable pages.
 * ORDER MATTERS! If there are multiple pattern matches, the first definition
 *   will take effect.
 *
 * @type {Array} List of {
 *   page: <String> human-readable, unique identifier for the page
 *   pattern: <RegExp> window pathname that the definition applies to
 *   webToMobilePathTranslator: <Function> that changes the window pathname
 *     to mobile deep-link target pathname.
 * }
 */
export const DEEP_LINKABLE_PAGES = [
  {
    page: 'browse-subdomain',
    pattern: /\/browse\/(.+)\/(.+)/,
    webToMobilePathTranslator: (path: string) => path,
  },
  {
    page: 'browse-domain',
    pattern: /\/browse\/([a-z]+)(.+)/,
    webToMobilePathTranslator: (path: string) => path,
  },
  {
    page: 'browse-root',
    pattern: /\/browse/,
    webToMobilePathTranslator: (path: string) => path.split('/').slice(0, 2).join('/'), // watch out for trailing '/'
  },
  {
    page: 'sdp',
    pattern: /\/specializations\/([a-z]+)(.+)/,
    webToMobilePathTranslator: (path: string) => path,
  },
  {
    page: 'ondemand-cdp-resource',
    pattern: /\/learn\/(.+)\/(supplement|exam|quiz|lecture)\/(.+)\/(.+)/,
    webToMobilePathTranslator: (path: string) => path.split('/').slice(0, 5).join('/'),
  },
  {
    page: 'ondemand-cdp-home-welcome',
    pattern: /\/learn\/(.+)\/home\/welcome/,
    webToMobilePathTranslator: (path: string) => path.split('/').slice(0, 5).join('/'),
  },
  {
    page: 'ondemand-cdp',
    pattern: /\/learn\/([^/]+)/,
    webToMobilePathTranslator: (path: string) => path,
  },
];

export const BANNER_COURSERA_LOGO_PATH = 'images/favicons/apple-touch-icon-144x144.png';

export const logInButtonProps = () => ({
  label: _t('Log In'),
  authMode: 'login' as const,
});

export const signUpButtonProps = () => ({
  label: _t('Join for Free'),
  authMode: 'signup' as const,
});

export const TRACKING_NAMES = {
  SHOPPING_CART: 'payment_cart_header' as const,
};
